import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, IconButton, InputAdornment, Link, makeStyles } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import classNames from 'classnames';
import { SGSButton, SGSForm, SGSFormInput } from 'components';
import SGSModalLight from 'components/SGSModalLight';
import { addServerErrors } from 'core/utils/formValidation';
import SmallFooter from 'features/utilisateurs/components/Footer';
import SmallHeader from 'features/utilisateurs/components/header';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import * as Yup from 'yup';
import secure from '../../assets/secure.svg';
import welcome from '../../assets/welcome.svg';
import { useAppContext } from '../../core/contexts';
import { auth } from '../../core/services';

const ResetPassword = () => {
    const history = useHistory();
    const { setToken } = useAppContext();
    const classes = useStyles();
    const queryClient = useQueryClient();
    const [showPassword, setShowPassword] = useState(false);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const resetPassword = () => history.push('/mdp-oublie');

    type Email = {
        mail: string;
        password: string;
        serverError?: string;
    };

    const validationSchema: Yup.SchemaOf<Email> = Yup.object().shape({
        mail: Yup.string().required(),
        password: Yup.string().required(),
        serverError: Yup.string(),
    });

    const methods = useForm<Email>({
        resolver: yupResolver(validationSchema),
        defaultValues: {},
    });

    const {
        handleSubmit,
        setError,
        formState: { errors },
    } = methods;

    const handleSendForm = (form, e) => {
        e.preventDefault();
        // Code fantôme : redirect to
        if (form.mail.includes('APP-')) {
            history.push('/connexion_backoffice?id=' + form.mail);
        } else {
            // Connexion
            setIsLoading(true);
            const customAxiosConfig = { displayErrorMsg: false };
            auth.authenticate(form.mail, form.password, null, customAxiosConfig)
                .then(async ({ token }) => {
                    // Reset token
                    await queryClient.removeQueries();
                    await queryClient.clear();
                    setToken(token);
                })
                .catch((error) => {
                    console.log(error);
                    if (error.response.data?.message === 'SSO token invalid') {
                        handleServerSideErrors('Email ou mot de passe invalide');
                    }
                    if (error.response.data?.code === 'adminAlreadySet') {
                        setIsOpenModal(true);
                    }
                })
                .finally(() => setIsLoading(false));
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleServerSideErrors = (message: string) => {
        addServerErrors(
            {
                serverError: [message], // Assuming 'error' is the field name
            },
            setError as any,
        );
    };

    return (
        <>
            <Box className={classes.container}>
                <SmallHeader displayBtnLogout={false} />
                <Box className={`${classes.row} ${classes.middleRow}`}>
                    <Grid container justifyContent="center">
                        <Grid item md={2} className={'p-5'}></Grid>

                        <Grid item md={5} className={classes.boxRight}>
                            <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                                <Box className={classes.logoSecure}>
                                    <img src={secure} alt={''} />
                                </Box>
                                <Box className="ml-3">
                                    <Box className={classes.sousTitre}>Accéder à l’espace entreprise</Box>
                                </Box>
                            </Box>

                            {errors?.serverError && <Box className={classes.errorMsg}>Email ou mot de passe invalide</Box>}

                            <Box className={classes.txt}>
                                <Box className="mt-2">
                                    <FormProvider {...methods}>
                                        <form className={classes.form} onSubmit={handleSubmit(handleSendForm)}>
                                            <Box className={classes.txtBlueBold}>Email :</Box>
                                            <SGSForm.Row>
                                                <SGSForm.Col>
                                                    <SGSFormInput variant="outlined" name="mail" />
                                                </SGSForm.Col>
                                            </SGSForm.Row>
                                            <Box className={classes.txtBlueBold}>Mot de passe :</Box>
                                            <SGSForm.Row>
                                                <SGSForm.Col>
                                                    <SGSFormInput
                                                        type={showPassword ? 'text' : 'password'}
                                                        variant="outlined"
                                                        name="password"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton onClick={handleTogglePasswordVisibility} className={classes.btnBlue}>
                                                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </SGSForm.Col>
                                            </SGSForm.Row>
                                            <Box className={classes.pwdOublie}>
                                                <Link onClick={() => resetPassword()} variant="body2" style={{ cursor: 'pointer' }} className={classes.emailLink}>
                                                    Mot de passe oublié
                                                </Link>
                                            </Box>
                                            <SGSButton type="submit" variant="contained" color="primary" className={classes.submit} loading={isLoading}>
                                                SE CONNECTER
                                            </SGSButton>
                                        </form>
                                    </FormProvider>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item md={3} className={'pl-5'}>
                            <Box className={classes.mobile}>
                                <Box>
                                    <img src={welcome} width="100%" alt={''} />
                                </Box>
                                <Box className={classes.boxInfo}>
                                    <Box className={classes.boxInfoText}>
                                        Un nouveau système d’authentification renforcé a été mis en place. Il vous sera demandé de renseigner un nouveau mot de passe sur de nouveaux critères pour le
                                        rendre plus sécurisé.{' '}
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item md={2} className={classNames(classes.mobile, 'p-5')}></Grid>
                    </Grid>
                </Box>
                <SmallFooter />
            </Box>
            <SGSModalLight isOpenModal={isOpenModal} setIsOpenModal={setIsOpenModal} title={'Oups, une erreur est survenue...'}>
                <Box
                    display={'flex'}
                    flex={1}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignContent={'center'}
                    alignItems={'center'}
                    style={{
                        minHeight: '100%', // This ensures the content is centered vertically within the viewport
                    }}
                >
                    <Box>Il existe déjà un administrateur pour ce compte !</Box>
                    <Box>Veuillez-vous rapprocher du BackOffice</Box>
                    <Box></Box>
                </Box>
            </SGSModalLight>
        </>
    );
};

export default ResetPassword;

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
    },
    row: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: '1px solid #ccc',
    },
    middleRow: {
        flex: 2,
    },
    titre: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: '36px',
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
    },
    sousTitre: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: '22px',
        fontStyle: 'normal',
        fontWeight: 600,
        marginTop: '10px',
        lineHeight: 'normal',
    },

    txt: {
        color: '#4F7793',
        fontFamily: 'Open Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        lineHeight: '26px',
        fontWeight: 300,
        maxWidth: '700px',
        marginTop: '30px',
        textAlign: 'justify',
    },
    txtBlueBold: {
        color: '#4F7793',
        fontFamily: 'Open Sans',
        fontSize: '18px',
        fontStyle: 'normal',
        lineHeight: '26px',
        fontWeight: 600,
        maxWidth: '700px',
        marginBottom: '10px',
    },
    paper: {
        paddingTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        color: '#FFF',
        textAlign: 'center',
        fontFamily: 'Open Sans',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 700,
        padding: '16px',
        borderRadius: '10px',
        width: '100%',
    },
    contactLink: {
        textDecoration: 'none', // Optional: Remove underline
        color: theme.palette.primary.main, // Optional: Use primary color
    },
    boxRight: {
        background: '#fff',
        paddingLeft: '50px',
        paddingRight: '50px',
        borderRadius: '10px 10px 0px 0px',
        maxWidth: '582px',
        padding: '20px',
        paddingBottom: '40px',
    },
    logoSecure: {
        background: '#fff',
        borderRadius: '50%',
        marginTop: '-80px',
        padding: '20px',
    },
    txtRed: {
        color: '#EB4C37',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    },
    txtGreen: {
        color: '#46BE81',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    },
    txtBlue: {
        color: '#4F7793',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
    },
    txtBlueUnderline: {
        color: '#4F7793',
        fontFamily: 'Segoe UI',
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
        textDecorationLine: 'underline',
    },
    emailLink: {
        color: '#2574A9',
        fontFamily: 'Open Sans',
        fontSize: 18,
        fontStyle: 'normal', // You can adjust this to 'italic' if needed
        fontWeight: 700,
        lineHeight: '26px',
        textDecorationLine: 'underline',
    },
    btnBlue: {
        color: '#4F7793',
        '&:hover': {
            color: '#4F7793',
        },
    },
    pwdOublie: { marginTop: '-15px', marginBottom: '20px', textAlign: 'right' },
    boxInfo: {
        mawWidth: '428px',
        width: '100%',
        height: '126px',
        flexShrink: 0,
        borderRadius: '8px',
        border: '0px solid #000',
        background: 'rgba(37, 116, 169, 0.10)',
        padding: '20px',
        marginTop: '20px',
        margin: 'auto',
        textAlign: 'center',
    },
    boxInfoText: { color: '#2574A9', fontFamily: 'Segoe UI', fontSize: '16px', fontStyle: 'normal', fontWeight: 400, lineHeight: 'normal', textAlign: 'justify' },
    errorMsg: {
        display: 'flex',
        justifyContent: 'center', // Horizontally center
        alignItems: 'center', // Vertically center
        marginTop: '20px',
        width: '470px',
        height: '50px',
        flexShrink: 0,
        borderRadius: '8px',
        border: '0px solid #EB4C37',
        background: 'rgba(235, 76, 55, 0.10)',
        color: '#EB4C37',
        fontFamily: 'Segoe UI',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
    },
    mobile: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
}));
